import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { X, Mail, Clock, User, Calendar, Shield } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { MEDIA_URL, authenticatedApi } from "@/services/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

// Define user type based on provided local storage data
type UserProfile = {
  id: string;
  organizationId: string;
  name: string;
  email: string;
  password: string;
  image: string | null;
  mobile: string;
  whatsApp: string | null;
  fcm: string | null;
  lastLogin: string | null;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

interface ProfileDetailCardProps {
  onClose: () => void; // Made required since we always show close button now
  showCloseButton?: boolean; // Keep for backward compatibility but ignored
}

const ProfileDetailCard = ({ onClose }: ProfileDetailCardProps) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        setIsLoading(true);
        // Use the getProfile API endpoint to fetch the current user's profile
        const response = await authenticatedApi.getProfile();
        setUserProfile(response.data);
        
        // Store profile data in localStorage for future quick access
        localStorage.setItem("manterflow-user-profile", JSON.stringify(response.data));
      } catch (error) {
        console.error("Error fetching user profile:", error);
        toast({
          title: "Error",
          description: "Failed to load user profile",
          variant: "destructive",
        });
        
        // Try to get from localStorage as fallback
        const profileData = localStorage.getItem("manterflow-user-profile");
        if (profileData) {
          setUserProfile(JSON.parse(profileData));
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserProfile();
  }, [toast]);

  // Loading skeleton UI
  if (isLoading) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="pb-2 border-b">
          <div className="flex items-center justify-between">
            <Skeleton className="h-6 w-32" />
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          <div className="flex flex-col sm:flex-row items-center gap-4">
            <Skeleton className="h-20 w-20 rounded-full" />
            <div className="space-y-2 text-center sm:text-left">
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
          <div className="space-y-4">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (!userProfile) {
    return (
      <Card className="w-full max-w-md mx-auto">
        <CardHeader className="pb-2 border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">User Profile</CardTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 text-center">
          <p className="text-muted-foreground">No profile data available</p>
          <Button 
            variant="outline" 
            className="mt-4"
            onClick={() => window.location.reload()}
          >
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Format dates for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      dateStyle: "medium",
      timeStyle: "short",
      hour12: true,
    });
  };

  // Format time ago
  const getTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSec = Math.round(diffMs / 1000);
    const diffMin = Math.round(diffSec / 60);
    const diffHour = Math.round(diffMin / 60);
    const diffDay = Math.round(diffHour / 24);

    if (diffSec < 60) return `${diffSec} seconds ago`;
    if (diffMin < 60) return `${diffMin} minutes ago`;
    if (diffHour < 24) return `${diffHour} hours ago`;
    if (diffDay < 30) return `${diffDay} days ago`;
    
    return formatDate(dateString);
  };

  // Capitalize role for display
  const formatRole = (role: string) => {
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  return (
    <Card className="profile-card w-full max-w-3xl mx-auto overflow-hidden">
      {/* Header with close button */}
      <CardHeader className="bg-background pb-2 border-b">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <User className="h-4 w-4 text-primary" />
            Profile Details
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800"
            onClick={onClose}
            aria-label="Close profile"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Hero section with avatar and name */}
        <div className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
            <Avatar className="h-20 w-20 sm:h-24 sm:w-24 border-4 border-white shadow-lg">
              <AvatarImage
                src={
                  userProfile.image
                    ? `${MEDIA_URL}${userProfile.image}`
                    : "/placeholder.svg"
                }
                alt={userProfile.name}
              />
              <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-600 text-white text-xl font-semibold">
                {userProfile.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="text-center sm:text-left space-y-1">
              <h2 className="text-xl sm:text-2xl font-bold">
                {userProfile.name}
              </h2>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                <Badge 
                  variant="outline" 
                  className={cn(
                    "bg-white/80 backdrop-blur-sm border border-white/20 shadow-sm",
                    "px-2 py-1 text-xs font-medium"
                  )}
                >
                  <Shield className="h-3 w-3 mr-1" />
                  {formatRole(userProfile.role)}
                </Badge>
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  <Mail className="h-3 w-3 inline-block mr-1" />
                  {userProfile.email}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Details section */}
        <div className="p-6 space-y-6">
          {/* Last login card */}
          <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-4 border border-slate-100 dark:border-slate-700">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h3 className="text-sm font-medium">Last Login</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {userProfile.lastLogin
                    ? getTimeAgo(userProfile.lastLogin)
                    : "Never logged in"}
                </p>
              </div>
            </div>
          </div>

          {/* Contact information */}
          <div>
            <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Account Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              <div className="bg-white dark:bg-slate-800 p-3 rounded-md border border-slate-200 dark:border-slate-700">
                <Label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Email Address</Label>
                <p className="text-sm font-medium break-all">{userProfile.email}</p>
              </div>
              
              {/* Mobile */}
              <div className="bg-white dark:bg-slate-800 p-3 rounded-md border border-slate-200 dark:border-slate-700">
                <Label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Mobile Number</Label>
                <p className="text-sm font-medium">{userProfile.mobile}</p>
              </div>
              
              {/* Created date */}
              <div className="bg-white dark:bg-slate-800 p-3 rounded-md border border-slate-200 dark:border-slate-700">
                <Label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Account Created</Label>
                <p className="text-sm font-medium">{formatDate(userProfile.createdAt)}</p>
              </div>
              
              {/* Status */}
              <div className="bg-white dark:bg-slate-800 p-3 rounded-md border border-slate-200 dark:border-slate-700">
                <Label className="text-xs text-slate-500 dark:text-slate-400 block mb-1">Account Status</Label>
                <Badge 
                  className={cn(
                    "mt-1",
                    userProfile.isActive 
                      ? "bg-green-100 text-green-800 hover:bg-green-100" 
                      : "bg-red-100 text-red-800 hover:bg-red-100"
                  )}
                  variant="outline"
                >
                  {userProfile.isActive ? "Active" : "Inactive"}
                </Badge>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// For backward compatibility
const ProfileCard = ProfileDetailCard;
export default ProfileDetailCard;
export { ProfileCard };
